import { useEffect, useState, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { DocumentForm, isFieldVisible, isSectionVisible } from "@/components/DocumentForm";
import { SignaturePad } from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FileText, Plus, Loader2, ArrowLeft, Save, Send, PenTool, RotateCcw, CheckCircle2, Copy, Clock, Download, FileDown, RefreshCw, AlertTriangle, Trash2, FileUp, StickyNote } from "lucide-react";
import { ViewCountBadge } from "@/components/ViewCountBadge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { fmtLogDate, fmtLogDay, orgToday } from "@/lib/tz";
import { useAutoSave } from "@/hooks/useAutoSave";
import { SaveIndicator } from "@/components/SaveIndicator";

const CRIMSON = "#bf3b4f";
const LEGACY_SOURCES = ["My Prydes Support (Legacy)", "Shift Care", "Flow Logic", "Good Human", "Other"];
async function fileToDataUrl(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
}
const STATE_STYLE = {
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  "Awaiting Signature": "bg-amber-50 text-amber-700 border-amber-200",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Archived: "bg-slate-100 text-slate-400 border-slate-200",
};

export function CarePlansTab({ participant, categoryId, onChanged }) {
  const { can } = useAuth();
  const [docs, setDocs] = useState(null);
  const [stats, setStats] = useState({});
  const [current, setCurrent] = useState(null); // full instance
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [pick, setPick] = useState("");
  const [mode, setMode] = useState("new");   // new | upload
  const [up, setUp] = useState({ source_system: "", start_date: "", end_date: "" });
  const [officeFile, setOfficeFile] = useState(null);
  const [workerFile, setWorkerFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendMode, setSendMode] = useState("send");
  const [signOpen, setSignOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [drift, setDrift] = useState(false);
  const [dates, setDates] = useState({ start_date: "", end_date: "" });
  const [savingDates, setSavingDates] = useState(false);
  const [officeNotes, setOfficeNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [dl, setDl] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => api.participantDocuments(participant)
    .then((all) => setDocs(categoryId ? all.filter((d) => d.category_id === categoryId) : all))
    .catch(() => { setDocs([]); toast.error("Could not load documents."); })
    .finally(() => onChanged?.());
  useEffect(() => { setCurrent(null); load(); api.participantDocumentViewStats(participant).then(setStats).catch(() => {}); /* eslint-disable-next-line */ }, [participant, categoryId]);

  const openDoc = async (id) => {
    try {
      const d = await api.documentInstance(id);
      setCurrent(d); setValues(d.values || {}); setDrift(false);
      setDates({ start_date: d.start_date || "", end_date: d.end_date || "" });
      setOfficeNotes(d.office_notes || "");
      if (d.workflow_state === "Draft" && can.has("participant_docs.edit")) {
        api.refreshDocumentContacts(id, false).then((r) => setDrift(!!r.changed)).catch(() => {});
      }
    } catch { toast.error("Could not open document."); }
  };

  const saveDates = async () => {
    setSavingDates(true);
    try {
      const d = await api.setDocumentDates(current.id, dates);
      setCurrent((c) => ({ ...c, start_date: d.start_date, end_date: d.end_date }));
      toast.success("Dates saved."); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    finally { setSavingDates(false); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const d = await api.updateDocumentNotes(current.id, officeNotes.trim() || null);
      setCurrent((c) => ({ ...c, office_notes: d.office_notes }));
      // Also update the list summary so the note indicator badge reflects immediately.
      setDocs((prev) => (prev || []).map((item) =>
        item.id === current.id ? { ...item, office_notes: d.office_notes } : item
      ));
      toast.success("Note saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    finally { setSavingNotes(false); }
  };

  // Admins can delete anything; editors only drafts never sent for signing or approved.
  const canDelete = (d) => can.has("participant_docs.delete_signed") || (can.has("participant_docs.delete_draft") && d.unsent_draft);

  const deleteDoc = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    try {
      await api.deleteDocument(id);
      toast.success("Document deleted.");
      if (current?.id === id) setCurrent(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed.");
    }
  };

  // Warn + auto-add to compliance when the type isn't yet required for this participant.
  const ensureRequired = async (templateId) => {
    const ref = `tmpl:${templateId}`;
    try {
      const c = await api.participantCompliance(participant);
      if ((c.items || []).some((i) => i.ref === ref)) return true;
      if (!window.confirm("This document type isn't in this participant's compliance requirements yet. "
        + "To add this document, it will also be added as a required document (affecting their compliance score). Continue?")) return false;
      await api.addRequirement(participant, ref);
      return true;
    } catch { return true; }  // never block on a compliance hiccup
  };

  const refreshFromProfile = async () => {
    setRefreshing(true);
    try {
      const r = await api.refreshDocumentContacts(current.id, true);
      // Always merge the returned values map into local state so profile-locked
      // fields (contacts, guardians, decision-makers) are immediately visible
      // in the form — even when the backend reports no change (the document was
      // already up to date but the fields may have been empty on first load).
      if (r.values) {
        setValues((prev) => ({ ...prev, ...r.values }));
      }
      setDrift(false);
      if (r.applied) {
        toast.success("Profile fields & contacts refreshed from the participant profile.");
      } else {
        toast.info("Profile fields & contacts are already up to date.");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Refresh failed."); }
    finally { setRefreshing(false); }
  };

  const openPicker = async () => {
    try {
      const all = await api.docTemplates();
      setTemplates(all.filter((t) => t.status === "Active" && (!categoryId || t.category_id === categoryId)));
      setPick(""); setMode("new"); setUp({ source_system: "", start_date: "", end_date: "" });
      setOfficeFile(null); setWorkerFile(null); setPickOpen(true);
    } catch { toast.error("Could not load templates."); }
  };

  const create = async () => {
    if (!pick) { toast.error("Choose a document type."); return; }
    if (!(await ensureRequired(pick))) return;
    setCreating(true);
    try {
      const d = await api.createDocument({ template_id: pick, participant });
      setPickOpen(false);
      await load();
      openDoc(d.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed."); }
    finally { setCreating(false); }
  };

  const doUpload = async () => {
    if (!pick) { toast.error("Choose a document type."); return; }
    if (!up.source_system) { toast.error("Choose the source system."); return; }
    if (!officeFile) { toast.error("An Office copy (PDF) is required."); return; }
    if (!(await ensureRequired(pick))) return;
    setCreating(true);
    try {
      const office_data = await fileToDataUrl(officeFile);
      const worker_data = workerFile ? await fileToDataUrl(workerFile) : null;
      const d = await api.uploadLegacyDocument({
        template_id: pick, participant, source_system: up.source_system,
        start_date: up.start_date, end_date: up.end_date,
        office_data, office_filename: officeFile.name,
        worker_data, worker_filename: workerFile?.name,
      });
      toast.success(workerFile ? "Uploaded — visible to office & support workers." : "Uploaded — hidden from support workers (no worker copy).");
      setPickOpen(false);
      await load();
      openDoc(d.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed."); }
    finally { setCreating(false); }
  };

  // ── Auto-save ──────────────────────────────────────────────────────────────
  const { queueAuto, flushAll, globalSave, resetStatus, hasPending } = useAutoSave();

  // Keep a stable ref to `values` so the queued lambda always captures the
  // latest value without needing to re-queue on every keystroke.
  const valuesRef = useRef(values);
  useEffect(() => { valuesRef.current = values; }, [values]);

  // When opening a new document clear any stale save-status from the previous one.
  useEffect(() => { resetStatus(); }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    // Flush any in-flight debounced save first to avoid a duplicate write
    // racing with the explicit save; explicit save provides the success toast.
    flushAll();
    for (const s of current.schema || []) {
      if (!isSectionVisible(s, values)) continue;
      for (const f of s.fields || [])
        if (f.required && isFieldVisible(f, values) && !(f.type === "checkbox" ? values[f.key] : String(values[f.key] ?? "").trim()))
          { toast.error(`${f.label} is required.`); return; }
    }
    setSaving(true);
    try { await api.updateDocument(current.id, { values }); toast.success("Document saved."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };
  const doApprove = async () => {
    if (!window.confirm("Approve this document? It will be locked and become the official record.")) return;
    setBusy(true);
    try {
      await api.approveDocument(current.id);
      toast.success("Document approved.");
      await openDoc(current.id);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed."); }
    finally { setBusy(false); }
  };
  const doRevert = async () => {
    if (!window.confirm("Revert to Draft? Any pending signature link will be invalidated.")) return;
    setBusy(true);
    try {
      await api.revertDocument(current.id);
      toast.success("Reverted to Draft.");
      await openDoc(current.id);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Revert failed."); }
    finally { setBusy(false); }
  };
  const refresh = () => openDoc(current.id);
  const uploadSigned = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Please upload the signed copy as a PDF."); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("File must be under 15 MB."); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
      });
      await api.uploadSignedCopy(current.id, { filename: file.name, data: dataUrl });
      toast.success("Signed copy uploaded. The office can now counter-sign.");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };
  const downloadSignedCopy = async () => {
    setDl("signed");
    try {
      const blob = await api.signedCopy(current.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(current.template_title || "document").replace(/\s+/g, "_")}_signed-copy.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not download the signed copy."); }
    finally { setDl(""); }
  };
  const downloadPdf = async (variant) => {
    setDl(variant);
    try {
      const blob = await api.documentPdf(current.id, variant);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(current.template_title || "document").replace(/\s+/g, "_")}_${variant}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not download PDF."); }
    finally { setDl(""); }
  };

  const downloadDraftPdf = async () => {
    setDl("draft");
    try {
      const blob = await api.documentDraftPdf(current.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeName = (current.template_title || "document").replace(/\s+/g, "_");
      const dateStamp = orgToday().replace(/-/g, "");
      a.href = url;
      a.download = `${safeName}_DRAFT_${dateStamp}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Draft PDF downloaded — not for signing.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate draft PDF."); }
    finally { setDl(""); }
  };

  const groups = useMemo(() => {
    const m = {};
    (docs || []).forEach((d) => {
      if (!m[d.template_id]) m[d.template_id] = { template_id: d.template_id, title: d.template_title, items: [] };
      m[d.template_id].items.push(d);
    });
    return Object.values(m);
  }, [docs]);

  if (current) {
    const locked = ["Approved", "Archived"].includes(current.workflow_state);
    const mode = current.signing_mode;
    const sg = current.signing || {};
    const state = current.workflow_state;
    const partSigned = !!sg.participant_signed_on;
    return (
      <div className="mt-4" data-testid="careplan-fill">
        <button onClick={() => {
          if (hasPending && !window.confirm("You have unsaved changes — leave without saving?")) return;
          setCurrent(null); load();
        }} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft className="h-3.5 w-3.5" /> All documents</button>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-base font-semibold text-slate-800">{current.template_title}</h2>
            <Badge className={`mt-1 text-[9px] py-0 ${STATE_STYLE[state] || STATE_STYLE.Draft}`}>{state}</Badge>
          </div>
          {can.has("participant_docs.edit") && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {state === "Draft" && <SaveIndicator st={globalSave} className="mr-1" />}
              {state === "Draft" && <Button onClick={save} disabled={saving} variant="outline" data-testid="careplan-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}Save
              </Button>}
              {!locked && !current.uploaded && (
                <Button
                  onClick={downloadDraftPdf}
                  disabled={dl === "draft"}
                  variant="outline"
                  className="border-amber-400 text-amber-800 hover:bg-amber-50 hover:border-amber-500 disabled:opacity-50"
                  data-testid="doc-download-draft"
                >
                  {dl === "draft" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileDown className="h-4 w-4 mr-1.5" />}Download Draft PDF
                </Button>
              )}
              {!locked && mode === "none" && <Button onClick={doApprove} disabled={busy} style={{ backgroundColor: CRIMSON }} data-testid="doc-approve"><CheckCircle2 className="h-4 w-4 mr-1.5" />Approve</Button>}
              {!locked && mode === "office" && <Button onClick={() => setSignOpen(true)} style={{ backgroundColor: CRIMSON }} data-testid="doc-office-sign"><PenTool className="h-4 w-4 mr-1.5" />Sign & approve</Button>}
              {!locked && mode === "both" && state === "Draft" && <Button onClick={() => { setSendMode("send"); setSendOpen(true); }} style={{ backgroundColor: CRIMSON }} data-testid="doc-send"><Send className="h-4 w-4 mr-1.5" />Send for signing</Button>}
              {!locked && mode === "both" && state === "Awaiting Signature" && !partSigned && sg.method !== "external" && <Button onClick={() => { setSendMode("resend"); setSendOpen(true); }} variant="outline" className="text-slate-600" data-testid="doc-resend"><RefreshCw className="h-4 w-4 mr-1.5" />Resend link</Button>}
              {!locked && mode === "both" && state === "Awaiting Signature" && partSigned && <Button onClick={() => setSignOpen(true)} style={{ backgroundColor: CRIMSON }} data-testid="doc-office-sign"><PenTool className="h-4 w-4 mr-1.5" />Office counter-sign</Button>}
              {!locked && state === "Awaiting Signature" && <Button onClick={doRevert} disabled={busy} variant="outline" className="text-slate-500" data-testid="doc-revert"><RotateCcw className="h-4 w-4 mr-1.5" />Revert</Button>}
              {canDelete(current) && <Button onClick={(e) => deleteDoc(current.id, e)} variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="doc-delete"><Trash2 className="h-4 w-4 mr-1.5" />Delete</Button>}
            </div>
          )}
        </div>

        {current.uploaded && (
          <div className="rounded-md bg-sky-50 border border-sky-200 p-2.5 text-[11px] text-sky-800 mb-3 flex items-center gap-2" data-testid="doc-uploaded-banner">
            <FileUp className="h-3.5 w-3.5 shrink-0" />
            Uploaded from {current.source_system || "a previous system"}. {current.has_worker ? "Visible to office & support workers." : "Hidden from support workers (no worker copy uploaded)."}
          </div>
        )}

        {can.has("participant_docs.edit") && (
          <div className="flex flex-wrap items-end gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 mb-3" data-testid="doc-dates-bar">
            <div><Label className="text-[10px] font-semibold text-slate-500">Start date</Label>
              <Input type="date" value={dates.start_date} onChange={(e) => {
                const next = { ...dates, start_date: e.target.value };
                setDates(next);
                if (current.id) queueAuto("doc-dates", () => api.setDocumentDates(current.id, next).then((d) => setCurrent((c) => ({ ...c, start_date: d.start_date, end_date: d.end_date }))));
              }} className="h-8 text-xs mt-0.5" data-testid="doc-start-date" /></div>
            <div><Label className="text-[10px] font-semibold text-slate-500">Expiry / review date</Label>
              <Input type="date" value={dates.end_date} onChange={(e) => {
                const next = { ...dates, end_date: e.target.value };
                setDates(next);
                if (current.id) queueAuto("doc-dates", () => api.setDocumentDates(current.id, next).then((d) => setCurrent((c) => ({ ...c, start_date: d.start_date, end_date: d.end_date }))));
              }} className="h-8 text-xs mt-0.5" data-testid="doc-end-date" /></div>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={saveDates} disabled={savingDates} data-testid="doc-dates-save">
              {savingDates ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save dates"}
            </Button>
            <span className="text-[10px] text-slate-400 ml-auto self-center">Used for compliance expiry tracking.</span>
          </div>
        )}

        {/* ── Office notes bar — distinct amber styling, clearly internal-only ── */}
        {can.has("participant_docs.edit") && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 mb-3" data-testid="doc-notes-bar">
            <div className="flex items-center gap-1.5 mb-2">
              <StickyNote className="h-3.5 w-3.5 text-amber-600 shrink-0" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">Office note</span>
              <span className="text-[10px] text-amber-500 ml-1">· internal only — not visible to participants or support workers</span>
            </div>
            <Textarea
              rows={3}
              value={officeNotes}
              onChange={(e) => {
                setOfficeNotes(e.target.value);
                if (current.id) queueAuto("doc-notes", () => api.updateDocumentNotes(current.id, e.target.value.trim() || null));
              }}
              placeholder="Add an internal note about this document (not shown to workers or the participant)…"
              className="text-xs resize-none bg-white border-amber-200 focus:border-amber-400 focus:ring-amber-200 min-h-[64px]"
              data-testid="doc-notes-textarea"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-[10px] text-amber-500">Editable at any time without affecting the document content or signature.</span>
              <Button size="sm" className="h-7 text-xs px-3 bg-amber-600 hover:bg-amber-700 text-white" onClick={saveNotes} disabled={savingNotes} data-testid="doc-notes-save">
                {savingNotes ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}Save note
              </Button>
            </div>
          </div>
        )}

        {state === "Awaiting Signature" && (
          sg.method === "external" ? (
            <div className="rounded-md p-3 text-[11px] mb-3 border bg-amber-50 border-amber-200 text-amber-800" data-testid="doc-physical-signing">
              <div className="flex items-center gap-2 font-medium">
                <Clock className="h-3.5 w-3.5 shrink-0" />
                {partSigned
                  ? `Signed copy uploaded${sg.signed_copy_uploaded_at ? ` ${fmtLogDate(sg.signed_copy_uploaded_at)}` : ""} — ready for the office to counter-sign.`
                  : "Out for physical / OPG signing. Print or send the signing PDF, then upload the signed copy once it's returned."}
              </div>
              {can.has("participant_docs.edit") && (
                <div className="flex flex-wrap gap-2 mt-2.5">
                  <Button size="sm" variant="outline" onClick={() => downloadPdf("signing")} disabled={dl === "signing"}
                    className="h-8 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="doc-download-signing">
                    {dl === "signing" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}Download signing PDF
                  </Button>
                  <label className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[13px] font-medium text-white cursor-pointer transition-opacity ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                    style={{ backgroundColor: CRIMSON }} data-testid="doc-upload-signed-label">
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />}{partSigned ? "Replace signed copy" : "Upload signed copy"}
                    <input type="file" accept="application/pdf" className="hidden"
                      onChange={(e) => { uploadSigned(e.target.files?.[0]); e.target.value = ""; }} data-testid="doc-upload-signed" />
                  </label>
                  {partSigned && (
                    <Button size="sm" variant="outline" onClick={downloadSignedCopy} disabled={dl === "signed"}
                      className="h-8 border-slate-300 text-slate-600 hover:bg-slate-100" data-testid="doc-download-signed-copy">
                      {dl === "signed" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}View signed copy
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className={`rounded-md p-2.5 text-[11px] mb-3 flex items-center gap-2 border ${sg.expired ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="doc-awaiting">
              {sg.expired ? <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> : <Clock className="h-3.5 w-3.5 shrink-0" />}
              {sg.expired ? "The signing link expired (not signed within 5 days). Click Resend link to send a fresh one." :
                partSigned ? "Participant has signed — ready for the office to counter-sign." :
                `Sent for signing${sg.sent_to_name ? ` to ${sg.sent_to_name}` : ""} via ${sg.method}${sg.reminders_sent ? ` · ${sg.reminders_sent} reminder(s) sent` : ""}. Awaiting participant signature.`}
            </div>
          )
        )}
        {locked && (
          <div className={`rounded-md border p-3 mb-3 ${state === "Archived" ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="doc-locked-banner">
            <p className={`text-[11px] mb-2 flex items-start gap-1.5 ${state === "Archived" ? "text-amber-800" : "text-emerald-700"}`}>
              {state === "Archived" && <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />}
              {state === "Archived"
                ? "This is an archived version — it is no longer the current document and has been replaced by a newer version. You can still view and download it below for reference."
                : `This document is signed/approved and locked${sg.office_signed_on ? ` — office signed ${fmtLogDay(sg.office_signed_on)}` : ""}. Download the official PDF record below.`}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => downloadPdf("office")} disabled={dl === "office"} className="border-slate-300 text-slate-600 hover:bg-slate-100" data-testid="doc-pdf-office">
                {dl === "office" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}Download PDF (Office)
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadPdf("worker")} disabled={dl === "worker"} className="border-slate-300 text-slate-600 hover:bg-slate-100" data-testid="doc-pdf-worker">
                {dl === "worker" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}Download PDF (Support Worker)
              </Button>
              {current.has_original && (
                <Button size="sm" variant="outline" onClick={() => downloadPdf("original")} disabled={dl === "original"} className="border-slate-300 text-slate-600 hover:bg-slate-100" data-testid="doc-pdf-original">
                  {dl === "original" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}Original record (imported)
                </Button>
              )}
              {sg.signed_copy_url && (
                <Button size="sm" variant="outline" onClick={downloadSignedCopy} disabled={dl === "signed"} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="doc-pdf-signed-copy">
                  {dl === "signed" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}Signed copy (uploaded)
                </Button>
              )}
            </div>
          </div>
        )}

        {["Draft", "Awaiting Signature"].includes(state) && can.has("participant_docs.edit") && !locked && !partSigned && (
          <div className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-xs ${drift ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"}`} data-testid="refresh-contacts-bar">
            <span className={`flex items-center gap-1.5 ${drift ? "text-amber-700 font-medium" : "text-slate-500"}`}>
              {drift
                ? <><AlertTriangle className="h-3.5 w-3.5" />The participant's contacts have changed since this document was created.</>
                : <><RefreshCw className="h-3.5 w-3.5" />Profile-linked fields &amp; contacts are kept in sync with the participant profile.</>}
            </span>
            <Button size="sm" variant="outline" onClick={refreshFromProfile} disabled={refreshing} className="h-7 text-xs shrink-0" data-testid="refresh-contacts-btn">
              {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}Refresh from profile
            </Button>
          </div>
        )}

        <DocumentForm schema={current.schema} values={values} onChange={(next) => {
          setValues(next);
          // Auto-save document fields on every change (Draft only, not locked)
          if (!locked && can.has("participant_docs.edit") && state !== "Awaiting Signature" && current.id) {
            queueAuto("doc-values", () => api.updateDocument(current.id, { values: next }));
          }
        }} disabled={locked || !can.has("participant_docs.edit") || state === "Awaiting Signature"} />

        <SendDialog open={sendOpen} onOpenChange={setSendOpen} doc={current} mode={sendMode} onSent={refresh} />
        <OfficeSignDialog open={signOpen} onOpenChange={setSignOpen} docId={current.id} onSigned={refresh} />
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3" data-testid="careplans-list">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Care plans & documents</div>
        {can.has("participant_docs.edit") && <Button size="sm" onClick={openPicker} style={{ backgroundColor: CRIMSON }} data-testid="new-careplan"><Plus className="h-4 w-4 mr-1" />New document</Button>}
      </div>
      {docs === null ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
        <div className="space-y-4">
          {docs.length === 0 && <div className="text-xs text-slate-400 py-4">No documents yet.</div>}
          {groups.map((g) => <DocGroup key={g.template_id} group={g} onOpen={openDoc} canDelete={canDelete} onDelete={deleteDoc} stats={stats} />)}
        </div>
      )}

      <Dialog open={pickOpen} onOpenChange={setPickOpen}>
        <DialogContent data-testid="pick-template-dialog">
          <DialogHeader><DialogTitle className="text-base font-brand">New document</DialogTitle></DialogHeader>
          <div className="flex gap-2 mb-1">
            <button onClick={() => setMode("new")} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium ${mode === "new" ? "border-[#bf3b4f] bg-rose-50 text-[#bf3b4f]" : "border-slate-200 text-slate-600"}`} data-testid="mode-new">Create new</button>
            <button onClick={() => setMode("upload")} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium ${mode === "upload" ? "border-[#bf3b4f] bg-rose-50 text-[#bf3b4f]" : "border-slate-200 text-slate-600"}`} data-testid="mode-upload">Upload from a previous system</button>
          </div>
          <Label className="text-[11px] font-semibold text-slate-600">Document type</Label>
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger className="h-9 text-sm" data-testid="pick-template"><SelectValue placeholder="Choose a document…" /></SelectTrigger>
            <SelectContent>
              {templates.map((t) => <SelectItem key={t.id} value={t.id} className="text-sm">{t.title}</SelectItem>)}
              {templates.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">No active templates. Create one in Settings → Documents.</div>}
            </SelectContent>
          </Select>

          {mode === "upload" && (
            <div className="space-y-3 mt-1">
              <div>
                <Label className="text-[11px] font-semibold text-slate-600">Created in (source system)</Label>
                <Select value={up.source_system || ""} onValueChange={(v) => setUp((u) => ({ ...u, source_system: v }))}>
                  <SelectTrigger className="h-9 text-sm mt-1" data-testid="upload-source"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{LEGACY_SOURCES.map((s) => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[11px] font-semibold text-slate-600">Start date</Label>
                  <Input type="date" value={up.start_date} onChange={(e) => setUp((u) => ({ ...u, start_date: e.target.value }))} className="h-9 text-sm mt-1" data-testid="upload-start" /></div>
                <div><Label className="text-[11px] font-semibold text-slate-600">End / expiry date</Label>
                  <Input type="date" value={up.end_date} onChange={(e) => setUp((u) => ({ ...u, end_date: e.target.value }))} className="h-9 text-sm mt-1" data-testid="upload-end" /></div>
              </div>
              <div>
                <Label className="text-[11px] font-semibold text-slate-600">Office copy (PDF, required)</Label>
                <Input type="file" accept="application/pdf" onChange={(e) => setOfficeFile(e.target.files?.[0] || null)} className="h-9 text-sm mt-1" data-testid="upload-office-file" />
              </div>
              <div>
                <Label className="text-[11px] font-semibold text-slate-600">Support Worker copy (PDF, optional)</Label>
                <Input type="file" accept="application/pdf" onChange={(e) => setWorkerFile(e.target.files?.[0] || null)} className="h-9 text-sm mt-1" data-testid="upload-worker-file" />
                <p className="text-[10px] text-slate-400 mt-1">Leave blank to keep this document hidden from support workers.</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPickOpen(false)}>Cancel</Button>
            <Button onClick={mode === "upload" ? doUpload : create} disabled={creating || !pick} style={{ backgroundColor: CRIMSON }} data-testid="pick-template-create">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : mode === "upload" ? <FileUp className="h-4 w-4 mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}{mode === "upload" ? "Upload" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SendDialog({ open, onOpenChange, doc, mode, onSent }) {
  const isResend = mode === "resend";
  const [method, setMethod] = useState("email");
  const [recipientKey, setRecipientKey] = useState("");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");

  useEffect(() => {
    if (!open) return;
    setLink(""); setMethod(isResend && doc.signing?.method ? doc.signing.method : "email");
    setLoading(true);
    api.participantSigningContacts(doc.participant)
      .then((r) => { setContacts(r.contacts || []); setRecipientKey((r.contacts || [])[0]?.key || ""); })
      .catch(() => toast.error("Could not load contacts."))
      .finally(() => setLoading(false));
  // eslint-disable-next-line
  }, [open, doc.participant]);

  const selected = contacts.find((c) => c.key === recipientKey) || null;
  const preview = method === "email" ? selected?.email : method === "sms" ? selected?.mobile : "";
  const previewMissing = (method === "email" || method === "sms") && selected && !preview;

  const send = async () => {
    if (method !== "external" && method !== "portal") {
      if (!selected) { toast.error("Choose a recipient."); return; }
      if (method === "email" && !selected.email) { toast.error("This contact has no email on record. Pick SMS or another contact."); return; }
      if (method === "sms" && !selected.mobile) { toast.error("This contact has no mobile on record. Pick Email or another contact."); return; }
    }
    const body = (method === "external" || method === "portal")
      ? { method }
      : { method, to_name: selected.name, to_email: selected.email || "", to_mobile: selected.mobile || "" };
    setBusy(true);
    try {
      const call = isResend ? api.resendSigning : api.sendForSigning;
      await call(doc.id, body);
      if (method === "portal") {
        toast.success("Ready for signing in the participant portal. The participant or a linked guardian can sign when they log in.");
        onOpenChange(false); onSent();
      } else if (method === "external") {
        // Download the blank participant/guardian signature PDF to print / send to the OPG.
        try {
          const blob = await api.documentPdf(doc.id, "signing");
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${(doc.template_title || "document").replace(/\s+/g, "_")}_for-signing.pdf`;
          document.body.appendChild(a); a.click(); a.remove();
          URL.revokeObjectURL(url);
        } catch { toast.error("Marked for signing, but the PDF didn't download — use 'Download signing PDF' on the document."); }
        toast.success("Ready for physical / OPG signing — PDF downloaded. Upload the signed copy once returned.");
        onOpenChange(false); onSent();
      } else {
        toast.success(`Signing invitation ${isResend ? "resent" : "sent"} to ${selected.name} via ${method === "sms" ? "SMS" : "Email"}. Link expires in 5 days.`);
        onOpenChange(false); onSent();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setLink(""); onOpenChange(v); }}>
      <DialogContent data-testid="send-dialog">
        <DialogHeader><DialogTitle className="text-base font-brand flex items-center gap-2"><Send className="h-4 w-4" style={{ color: CRIMSON }} />{isResend ? "Resend signing link" : "Send for signing"}</DialogTitle></DialogHeader>
        {link ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">Share this secure link with the recipient. They'll verify with their NDIS number & date of birth. Expires in 5 days.</p>
            <div className="flex gap-2">
              <Input value={link} readOnly className="h-9 text-xs" data-testid="send-link" />
              <Button onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied."); }} variant="outline" className="h-9"><Copy className="h-4 w-4" /></Button>
            </div>
            <DialogFooter><Button onClick={() => { onOpenChange(false); onSent(); }} style={{ backgroundColor: CRIMSON }}>Done</Button></DialogFooter>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-3">
            {isResend && <div className="rounded-md bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-800">A new signing link will be generated and the previous one invalidated. The 5-day expiry resets from now.</div>}
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Signing method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="send-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email" className="text-sm">Email a signing link</SelectItem>
                  <SelectItem value="sms" className="text-sm">SMS a signing link</SelectItem>
                  <SelectItem value="portal" className="text-sm">Participant Portal (they sign when logged in)</SelectItem>
                  <SelectItem value="external" className="text-sm">OPG / External / Physical (download PDF to sign)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {method !== "external" && method !== "portal" && (
              <>
                <div>
                  <Label className="text-[11px] font-semibold text-slate-600">Send to</Label>
                  <Select value={recipientKey} onValueChange={setRecipientKey}>
                    <SelectTrigger className="mt-1 h-9 text-sm" data-testid="send-recipient"><SelectValue placeholder="Choose a contact…" /></SelectTrigger>
                    <SelectContent>
                      {contacts.map((c) => <SelectItem key={c.key} value={c.key} className="text-sm">{c.name} ({c.relationship})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] font-semibold text-slate-600">Will be sent to</Label>
                  <div className={`mt-1 h-9 flex items-center px-3 rounded-md border text-sm ${previewMissing ? "border-red-200 bg-red-50 text-red-600" : "border-slate-200 bg-slate-50 text-slate-700"}`} data-testid="send-preview">
                    {preview || (previewMissing ? `⚠️ No ${method === "email" ? "email" : "mobile"} on record` : "—")}
                  </div>
                </div>
              </>
            )}
            {method === "portal" && <div className="rounded-md bg-teal-50 border border-teal-200 p-2.5 text-[11px] text-teal-800">No email or SMS is sent. The document becomes available to sign in the participant portal — the participant or any linked guardian/decision-maker with portal access can review and sign it when they log in.</div>}
            {method === "external" && <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5 text-[11px] text-slate-600">No email or SMS is sent. A PDF with a blank participant/guardian signature section downloads now — print it or send it to the OPG. Once it's signed and returned, upload the signed copy on the document and the office can counter-sign.</div>}
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={send} disabled={busy} style={{ backgroundColor: CRIMSON }} data-testid="send-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : method === "external" ? <Download className="h-4 w-4 mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}{method === "external" ? "Download signing PDF" : method === "portal" ? "Make available in portal" : isResend ? "Resend" : "Send"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OfficeSignDialog({ open, onOpenChange, docId, onSigned }) {
  const [sig, setSig] = useState("");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const sign = async () => {
    if (!sig) { toast.error("Please draw your signature."); return; }
    if (!typed.trim()) { toast.error("Please type your name."); return; }
    setBusy(true);
    try { await api.officeSign(docId, { signature: sig, signature_text: typed.trim() }); toast.success("Signed & approved."); onOpenChange(false); setSig(""); setTyped(""); onSigned(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="office-sign-dialog">
        <DialogHeader><DialogTitle className="text-base font-brand flex items-center gap-2"><PenTool className="h-4 w-4" style={{ color: CRIMSON }} />Office signature</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">Signing approves this document. It will lock and become the official record.</p>
        <SignaturePad onChange={setSig} />
        <div>
          <Label className="text-[11px] font-semibold text-slate-600">Your name</Label>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type your full name" className="mt-1 h-9 text-sm" data-testid="office-sign-name" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={sign} disabled={busy} style={{ backgroundColor: CRIMSON }} data-testid="office-sign-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <PenTool className="h-4 w-4 mr-1.5" />}Sign & approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const PAGE_SIZE = 4;
function DocGroup({ group, onOpen, canDelete, onDelete, stats }) {
  const [page, setPage] = useState(0);
  const total = group.items.length;
  const pages = Math.ceil(total / PAGE_SIZE);
  const start = page * PAGE_SIZE;
  const slice = group.items.slice(start, start + PAGE_SIZE);
  return (
    <div data-testid={`doc-group-${group.template_id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{group.title}</div>
        <span className="text-[10px] text-slate-400">{total} document{total === 1 ? "" : "s"}</span>
      </div>
      <div className="space-y-2">
        {slice.map((d) => (
          <div key={d.id} role="button" tabIndex={0} onClick={() => onOpen(d.id)} data-testid={`careplan-${d.id}`}
            className="w-full cursor-pointer text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-[#bf3b4f] transition-all flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-medium text-slate-800">{d.title || d.template_title}</div>
              <div className="text-[10px] text-slate-400 flex items-center gap-2">
                <span>Updated {fmtLogDate(d.updated_at)}</span>
                <ViewCountBadge stat={stats?.[`internal:${d.id}`]} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              {d.migrated && <Badge className="bg-sky-50 text-sky-600 border-sky-200 text-[9px] py-0" title="Imported from legacy system">Migrated</Badge>}
              {d.signed_pdf && <Badge className="bg-indigo-50 text-indigo-600 border-indigo-200 text-[9px] py-0">PDF</Badge>}
              {d.office_notes && (
                <span title={`Office note: ${d.office_notes}`} className="flex items-center" data-testid={`careplan-has-note-${d.id}`}>
                  <StickyNote className="h-3.5 w-3.5 text-amber-400" />
                </span>
              )}
              <Badge className={`text-[9px] py-0 ${STATE_STYLE[d.workflow_state] || STATE_STYLE.Draft}`}>{d.workflow_state}</Badge>
              {canDelete(d) && (
                <button onClick={(e) => onDelete(d.id, e)} title={d.unsent_draft ? "Delete draft" : "Delete document (admin)"}
                  className="text-slate-300 hover:text-rose-600 p-0.5" data-testid={`careplan-delete-${d.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-2" data-testid={`doc-group-pager-${group.template_id}`}>
          <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
            className="px-2 py-0.5 text-[11px] rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30">Prev</button>
          {Array.from({ length: pages }).map((_, i) => (
            <button key={i} onClick={() => setPage(i)}
              className={`w-6 h-6 text-[11px] rounded ${i === page ? "bg-[#bf3b4f] text-white" : "text-slate-500 hover:bg-slate-100"}`}
              data-testid={`doc-group-page-${group.template_id}-${i + 1}`}>{i + 1}</button>
          ))}
          <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page === pages - 1}
            className="px-2 py-0.5 text-[11px] rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30">Next</button>
        </div>
      )}
    </div>
  );
}
